import React, { useEffect, useState } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import { Button, Card, Form, Alert, Row, Col } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath, CLIENT_TYPES, US_STATES } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";

const PageContainer = styled.div`
  max-width: 800px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${colors.nightlyWoods700};
  text-decoration: none;
  margin-bottom: 16px;
  &:hover {
    color: ${colors.nightlyWoods900};
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 24px;
  color: ${colors.nightlyWoods900};
`;

const FormCard = styled(Card)`
  margin-bottom: 24px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

export const ClientEdit = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    client_type: "district",
    state: "",
    nces_lea_id: "",
    state_organization_id: "",
    hubspot_company_id: "",
    hubspot_deal_id: "",
    billing_email: "",
    billing_contact_name: "",
    notes: "",
  });

  useEffect(() => {
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    setLoading(true);
    setError(null);
    try {
      const client = await request(`admin/clients/${id}`, { method: "GET" });
      setFormData({
        name: client.name || "",
        client_type: client.client_type || "district",
        state: client.state || "",
        nces_lea_id: client.nces_lea_id || "",
        state_organization_id: client.state_organization_id || "",
        hubspot_company_id: client.hubspot_company_id || "",
        hubspot_deal_id: client.hubspot_deal_id || "",
        billing_email: client.billing_email || "",
        billing_contact_name: client.billing_contact_name || "",
        notes: client.notes || "",
      });
    } catch (err) {
      console.error("Failed to fetch client:", err);
      setError("Failed to load client. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Name is required.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach(key => {
        if (payload[key] === "") payload[key] = null;
      });
      payload.name = formData.name.trim();

      await request(`admin/clients/${id}`, {
        method: "PUT",
        payload,
      });
      history.push(generateRoutePath(constants.CLIENT_ADMIN_DETAIL_ROUTE_NAME, { id }));
    } catch (err) {
      console.error("Failed to update client:", err);
      setError("Failed to update client. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    history.push(generateRoutePath(constants.CLIENT_ADMIN_DETAIL_ROUTE_NAME, { id }));
  };

  if (loading) {
    return (
      <LoadingContainer>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
      </LoadingContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to={generateRoutePath(constants.CLIENT_ADMIN_DETAIL_ROUTE_NAME, { id })}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Client
      </BackLink>

      <PageTitle>Edit Client</PageTitle>

      {error && <Alert variant="danger">{error}</Alert>}

      <FormCard>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Type *</Form.Label>
                  <Form.Select name="client_type" value={formData.client_type} onChange={handleChange}>
                    {CLIENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Select name="state" value={formData.state} onChange={handleChange}>
                    <option value="">Select state...</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>NCES LEA ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="nces_lea_id"
                    value={formData.nces_lea_id}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State Organization ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="state_organization_id"
                    value={formData.state_organization_id}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>HubSpot Company ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="hubspot_company_id"
                    value={formData.hubspot_company_id}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>HubSpot Deal ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="hubspot_deal_id"
                    value={formData.hubspot_deal_id}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="billing_email"
                    value={formData.billing_email}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Billing Contact Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="billing_contact_name"
                    value={formData.billing_contact_name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Form.Group>

            <ButtonGroup>
              <Button variant="outline-secondary" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                style={{ backgroundColor: colors.nightlyWoods500, borderColor: colors.nightlyWoods500 }}
              >
                {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : "Save Changes"}
              </Button>
            </ButtonGroup>
          </Form>
        </Card.Body>
      </FormCard>
    </PageContainer>
  );
};
